<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-6 py-2">
          <h1 class="text-h4 text-primary mb-0 px-2">{{ $t('financialReports.title') }}</h1>
          <div>
            <v-btn variant="outlined" class="mr-2" @click="generateMonthlyReport">
              {{ $t('financialReports.generateMonthly') }}
            </v-btn>
            <v-btn variant="outlined" class="mr-2" @click="generateWeeklyReport">
              {{ $t('financialReports.generateWeekly') }}
            </v-btn>
            <v-btn color="primary" @click="showCreateDialog = true">
              {{ $t('financialReports.create') }}
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-row>
      <v-col cols="12">
        <v-card variant="tonal" class="pa-4">
          <v-card-title class="text-h6 mb-4">{{ $t('financialReports.filters') }}</v-card-title>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filterReportType"
                :items="reportTypes"
                item-title="label"
                item-value="value"
                :label="$t('financialReports.reportType')"
                clearable
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filterDateFrom"
                :label="$t('financialReports.dateFrom')"
                type="date"
                density="compact"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filterDateTo"
                :label="$t('financialReports.dateTo')"
                type="date"
                density="compact"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-btn @click="loadReports" color="primary" block>
                {{ $t('common.search') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Reports List -->
    <v-row>
      <v-col cols="12">
        <v-card class="bg-white">
          <v-card-title>{{ $t('financialReports.list') }}</v-card-title>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="reports"
              :loading="loading"
              :items-per-page="10"
              :items-per-page-options="[10, 25, 50, 100]"
              class="elevation-0"
            >
              <template #item.report_type="{ item }">
                <v-chip :color="getReportTypeColor(item.report_type)" size="small">
                  {{ getReportTypeLabel(item.report_type) }}
                </v-chip>
              </template>
              <template #item.period_start="{ item }">
                {{ formatDate(item.period_start) }}
              </template>
              <template #item.period_end="{ item }">
                {{ formatDate(item.period_end) }}
              </template>
              <template #item.period_days="{ item }">
                {{ item.period_days }} {{ $t('financialReports.days') }}
              </template>
              <template #item.generated_at="{ item }">
                {{ item.generated_at ? formatDateTime(item.generated_at) : '-' }}
              </template>
              <template #item.actions="{ item }">
                <v-btn density="compact"
                  variant="plain"
                  icon="mdi-eye"
                  size="small"
                  @click="viewReport(item)"
                  :title="$t('financialReports.view')"
                />
                <v-btn density="compact"
                  variant="plain"
                  icon="mdi-refresh"
                  size="small"
                  @click="regenerateReport(item)"
                  :title="$t('financialReports.regenerate')"
                />
                <v-btn density="compact"
                  variant="plain"
                  color="error"
                  icon="mdi-delete"
                  size="small"
                  @click="deleteReport(item)"
                  :title="$t('common.delete')"
                />
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          {{ editingReport ? $t('financialReports.edit') : $t('financialReports.create') }}
        </v-card-title>
        <v-card-text>
          <v-form ref="formRef" v-model="formValid">
            <v-select
              v-model="form.report_type"
              :items="reportTypes"
              item-title="label"
              item-value="value"
              :label="$t('financialReports.reportType')"
              :rules="[rules.required]"
              density="compact"
            />
            <v-text-field
              v-model="form.period_start"
              :label="$t('financialReports.periodStart')"
              type="date"
              :rules="[rules.required]"
              density="compact"
              class="mt-2"
            />
            <v-text-field
              v-model="form.period_end"
              :label="$t('financialReports.periodEnd')"
              type="date"
              :rules="[rules.required]"
              density="compact"
              class="mt-2"
            />
            <v-textarea
              v-model="form.description"
              :label="$t('financialReports.description')"
              density="compact"
              class="mt-2"
              rows="3"
            />
            <v-checkbox
              v-model="form.generate"
              :label="$t('financialReports.generateAfterCreate')"
              density="compact"
              class="mt-2"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="closeDialog">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveReport" :disabled="!formValid">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Report Dialog -->
    <v-dialog v-model="showViewDialog" max-width="1200px" scrollable>
      <v-card>
        <v-sheet color="primary" class="px-6 py-4 rounded-t">
          <div class="text-h6 text-white">
            {{ $t('financialReports.reportDetails') }} - {{ getReportTypeLabel(selectedReport?.report_type) }}
          </div>
        </v-sheet>
        <v-card-text>
          <v-row v-if="selectedReport">
            <v-col cols="12">
              <v-card class="mb-3">
                <v-card-text class="py-3">
                  <div class="d-flex justify-space-between align-start flex-wrap">
                    <div class="mr-4 mb-2">
                      <div><strong>{{ $t('financialReports.period') }}:</strong></div>
                      <div>{{ formatPeriod(selectedReport.period_start, selectedReport.period_end) }}</div>
                    </div>
                    <div class="text-right mb-2">
                      <div><strong>{{ $t('financialReports.generatedAt') }}:</strong></div>
                      <div>{{ selectedReport.generated_at ? formatDateTime(selectedReport.generated_at) : '-' }}</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-divider class="my-4" />
          <div v-if="selectedReport?.data">
            <h3 class="mb-3">{{ $t('financialReports.reportData') }}</h3>
            
            <!-- Monthly Factual Report -->
            <div v-if="selectedReport.report_type === 'monthly_factual'">
              <v-tabs v-model="monthlyFactualTab" class="mb-4">
                <v-tab value="overview">{{ $t('financialReports.overviewTab') }}</v-tab>
                <v-tab value="wallets">{{ $t('financialReports.walletsTab') }}</v-tab>
              </v-tabs>
              <v-window v-model="monthlyFactualTab">
                <v-window-item value="overview">
                  <v-row class="align-stretch">
                    <!-- Expenses Section -->
                    <v-col cols="12" md="6" class="d-flex">
                      <v-card class="mb-3 flex-grow-1">
                        <v-card-title class="text-subtitle-1 pb-1">
                          {{ $t('financialReports.expenses') }}
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pt-4">
                          <div class="d-flex justify-space-between">
                            <span>{{ $t('financialReports.totalExpenses') }}:</span>
                            <strong class="text-error">{{ formatNumber(selectedReport.data.expenses) }}</strong>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Period Details Section -->
                    <v-col cols="12" md="6" class="d-flex">
                      <v-card class="mb-3 flex-grow-1">
                        <v-card-title class="text-subtitle-1 pb-1">
                          {{ $t('financialReports.periodDetails') }}
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pt-4">
                          <div class="d-flex justify-space-between mb-2">
                            <span>{{ $t('financialReports.periodDays') }}:</span>
                            <strong>{{ selectedReport.period_days }}</strong>
                          </div>
                          <div class="d-flex justify-space-between align-start">
                            <span>{{ $t('financialReports.description') }}:</span>
                            <strong class="text-right ml-4">{{ selectedReport.description || '-' }}</strong>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Transactions Section -->
                    <v-col cols="12" md="6" class="d-flex">
                      <v-card class="mb-3 flex-grow-1">
                        <v-card-title class="text-subtitle-1 pb-1">
                          {{ $t('financialReports.transactions') }}
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pt-4">
                          <div class="d-flex justify-space-between mb-2">
                            <span>{{ $t('financialReports.incoming') }}:</span>
                            <strong>{{ formatNumber(selectedReport.data.transactions?.incoming) }}</strong>
                          </div>
                          <div class="d-flex justify-space-between mb-2">
                            <span>{{ $t('financialReports.outgoing') }}:</span>
                            <strong>{{ formatNumber(selectedReport.data.transactions?.outgoing) }}</strong>
                          </div>
                          <div class="d-flex justify-space-between">
                            <span>{{ $t('financialReports.fees') }}:</span>
                            <strong>{{ formatNumber(selectedReport.data.transactions?.fees) }}</strong>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Settlements Section -->
                    <v-col cols="12" md="6" class="d-flex">
                      <v-card class="mb-3 flex-grow-1">
                        <v-card-title class="text-subtitle-1 pb-1">
                          {{ $t('financialReports.settlements') }}
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pt-4">
                          <div class="d-flex justify-space-between mb-2">
                            <span>{{ $t('financialReports.toWallet') }}:</span>
                            <strong class="text-success">{{ formatNumber(selectedReport.data.settlements?.to_wallet) }}</strong>
                          </div>
                          <div class="d-flex justify-space-between">
                            <span>{{ $t('financialReports.toInvestor') }}:</span>
                            <strong class="text-error">{{ formatNumber(selectedReport.data.settlements?.to_investor) }}</strong>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Summary Section -->
                    <v-col cols="12" md="6" class="d-flex">
                      <v-card class="mb-3 flex-grow-1">
                        <v-card-title class="text-subtitle-1 pb-1">
                          {{ $t('financialReports.summary') }}
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pt-4">
                          <div class="d-flex justify-space-between mb-2">
                            <span>{{ $t('financialReports.netIncome') }}:</span>
                            <strong>{{ formatNumber(selectedReport.data.summary?.net_income) }}</strong>
                          </div>
                          <div class="d-flex justify-space-between mb-2">
                            <span>{{ $t('financialReports.totalSettlements') }}:</span>
                            <strong>{{ formatNumber(selectedReport.data.summary?.total_settlements) }}</strong>
                          </div>
                          <v-divider class="my-2" />
                          <div class="d-flex justify-space-between">
                            <span><strong>{{ $t('financialReports.netChange') }}:</strong></span>
                            <strong class="text-h6">{{ formatNumber(selectedReport.data.summary?.net_change) }}</strong>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-window-item>
                <v-window-item value="wallets">
                  <!-- Wallets Section -->
                  <v-card class="bg-white">
                    <v-card-title class="text-subtitle-1">
                      {{ $t('financialReports.walletBalances') }}
                    </v-card-title>
                    <v-card-text>
                      <v-table density="compact" v-if="selectedReport.data.wallets?.length">
                        <thead>
                          <tr>
                            <th>{{ $t('financialReports.coin') }}</th>
                            <th>{{ $t('financialReports.balance') }}</th>
                            <th>{{ $t('financialReports.balanceUsdt') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="wallet in selectedReport.data.wallets" :key="wallet.wallet_id">
                            <td><strong>{{ wallet.coin }}</strong></td>
                            <td>{{ formatNumber(wallet.balance) }}</td>
                            <td>{{ formatNumber(wallet.balance_usdt) }}</td>
                          </tr>
                        </tbody>
                      </v-table>
                      <div v-else class="text-center pa-4 text-grey">
                        {{ $t('financialReports.noWallets') }}
                      </div>
                    </v-card-text>
                  </v-card>
                </v-window-item>
              </v-window>
            </div>

            <!-- Weekly Factual Report -->
            <div v-else-if="selectedReport.report_type === 'weekly_factual'">
              <v-row>
                <!-- Transactions Section -->
                <v-col cols="12" md="6">
                  <v-card variant="outlined" class="mb-3">
                    <v-card-title class="text-subtitle-1">
                      {{ $t('financialReports.transactions') }}
                    </v-card-title>
                    <v-card-text>
                      <div class="d-flex justify-space-between mb-2">
                        <span>{{ $t('financialReports.incoming') }}:</span>
                        <strong>{{ formatNumber(selectedReport.data.transactions?.incoming) }}</strong>
                      </div>
                      <div class="d-flex justify-space-between">
                        <span>{{ $t('financialReports.outgoing') }}:</span>
                        <strong>{{ formatNumber(selectedReport.data.transactions?.outgoing) }}</strong>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Expenses Section -->
                <v-col cols="12" md="6">
                  <v-card variant="outlined" class="mb-3">
                    <v-card-title class="text-subtitle-1">
                      {{ $t('financialReports.expenses') }}
                    </v-card-title>
                    <v-card-text>
                      <div class="d-flex justify-space-between">
                        <span>{{ $t('financialReports.totalExpenses') }}:</span>
                        <strong class="text-error">{{ formatNumber(selectedReport.data.expenses) }}</strong>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Settlements Section -->
                <v-col cols="12" md="6">
                  <v-card variant="outlined" class="mb-3">
                    <v-card-title class="text-subtitle-1">
                      {{ $t('financialReports.settlements') }}
                    </v-card-title>
                    <v-card-text>
                      <div class="d-flex justify-space-between mb-2">
                        <span>{{ $t('financialReports.toWallet') }}:</span>
                        <strong class="text-success">{{ formatNumber(selectedReport.data.settlements?.to_wallet) }}</strong>
                      </div>
                      <div class="d-flex justify-space-between">
                        <span>{{ $t('financialReports.toInvestor') }}:</span>
                        <strong class="text-error">{{ formatNumber(selectedReport.data.settlements?.to_investor) }}</strong>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Summary Section -->
                <v-col cols="12" md="6">
                  <v-card variant="tonal" class="mb-3" color="primary">
                    <v-card-title class="text-subtitle-1">
                      {{ $t('financialReports.summary') }}
                    </v-card-title>
                    <v-card-text>
                      <div class="d-flex justify-space-between mb-2">
                        <span>{{ $t('financialReports.netIncome') }}:</span>
                        <strong>{{ formatNumber(selectedReport.data.summary?.net_income) }}</strong>
                      </div>
                      <div class="d-flex justify-space-between">
                        <span>{{ $t('financialReports.totalSettlements') }}:</span>
                        <strong>{{ formatNumber(selectedReport.data.summary?.total_settlements) }}</strong>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Monthly Forecast Report -->
            <div v-else-if="selectedReport.report_type === 'monthly_forecast'">
              <v-alert type="info" class="mb-4" v-if="selectedReport.data.note">
                {{ selectedReport.data.note }}
              </v-alert>
              <v-row>
                <!-- Transactions Section -->
                <v-col cols="12" md="6">
                  <v-card variant="outlined" class="mb-3">
                    <v-card-title class="text-subtitle-1">
                      {{ $t('financialReports.forecastTransactions') }}
                    </v-card-title>
                    <v-card-text>
                      <div class="d-flex justify-space-between mb-2">
                        <span>{{ $t('financialReports.incoming') }}:</span>
                        <strong>{{ formatNumber(selectedReport.data.transactions?.incoming) }}</strong>
                      </div>
                      <div class="d-flex justify-space-between mb-2">
                        <span>{{ $t('financialReports.outgoing') }}:</span>
                        <strong>{{ formatNumber(selectedReport.data.transactions?.outgoing) }}</strong>
                      </div>
                      <div class="d-flex justify-space-between">
                        <span>{{ $t('financialReports.fees') }}:</span>
                        <strong>{{ formatNumber(selectedReport.data.transactions?.fees) }}</strong>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Expenses Section -->
                <v-col cols="12" md="6">
                  <v-card variant="outlined" class="mb-3">
                    <v-card-title class="text-subtitle-1">
                      {{ $t('financialReports.forecastExpenses') }}
                    </v-card-title>
                    <v-card-text>
                      <div class="d-flex justify-space-between">
                        <span>{{ $t('financialReports.totalExpenses') }}:</span>
                        <strong class="text-error">{{ formatNumber(selectedReport.data.expenses) }}</strong>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Settlements Section -->
                <v-col cols="12" md="6">
                  <v-card variant="outlined" class="mb-3">
                    <v-card-title class="text-subtitle-1">
                      {{ $t('financialReports.forecastSettlements') }}
                    </v-card-title>
                    <v-card-text>
                      <div class="d-flex justify-space-between mb-2">
                        <span>{{ $t('financialReports.toWallet') }}:</span>
                        <strong class="text-success">{{ formatNumber(selectedReport.data.settlements?.to_wallet) }}</strong>
                      </div>
                      <div class="d-flex justify-space-between">
                        <span>{{ $t('financialReports.toInvestor') }}:</span>
                        <strong class="text-error">{{ formatNumber(selectedReport.data.settlements?.to_investor) }}</strong>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Summary Section -->
                <v-col cols="12" md="6">
                  <v-card variant="tonal" class="mb-3" color="primary">
                    <v-card-title class="text-subtitle-1">
                      {{ $t('financialReports.forecastSummary') }}
                    </v-card-title>
                    <v-card-text>
                      <div class="d-flex justify-space-between mb-2">
                        <span>{{ $t('financialReports.netIncome') }}:</span>
                        <strong>{{ formatNumber(selectedReport.data.summary?.net_income) }}</strong>
                      </div>
                      <div class="d-flex justify-space-between mb-2">
                        <span>{{ $t('financialReports.totalSettlements') }}:</span>
                        <strong>{{ formatNumber(selectedReport.data.summary?.total_settlements) }}</strong>
                      </div>
                      <v-divider class="my-2" />
                      <div class="d-flex justify-space-between">
                        <span><strong>{{ $t('financialReports.netChange') }}:</strong></span>
                        <strong class="text-h6">{{ formatNumber(selectedReport.data.summary?.net_change) }}</strong>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Historical Data Section -->
                <v-col cols="12" v-if="selectedReport.data.historical_data">
                  <v-card variant="outlined">
                    <v-card-title class="text-subtitle-1">
                      {{ $t('financialReports.historicalData') }}
                    </v-card-title>
                    <v-card-text>
                      <div class="d-flex justify-space-between mb-2">
                        <span>{{ $t('financialReports.monthsAnalyzed') }}:</span>
                        <strong>{{ selectedReport.data.historical_data.months_analyzed }}</strong>
                      </div>
                      <div class="d-flex justify-space-between mb-2">
                        <span>{{ $t('financialReports.averageIncoming') }}:</span>
                        <strong>{{ formatNumber(selectedReport.data.historical_data.average_incoming) }}</strong>
                      </div>
                      <div class="d-flex justify-space-between mb-2">
                        <span>{{ $t('financialReports.averageOutgoing') }}:</span>
                        <strong>{{ formatNumber(selectedReport.data.historical_data.average_outgoing) }}</strong>
                      </div>
                      <div class="d-flex justify-space-between">
                        <span>{{ $t('financialReports.averageExpenses') }}:</span>
                        <strong>{{ formatNumber(selectedReport.data.historical_data.average_expenses) }}</strong>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Combined Report -->
            <div v-else-if="selectedReport.report_type === 'combined'">
              <v-tabs v-model="combinedTab" class="mb-4">
                <v-tab value="factual">{{ $t('financialReports.factual') }}</v-tab>
                <v-tab value="forecast">{{ $t('financialReports.forecast') }}</v-tab>
                <v-tab value="comparison">{{ $t('financialReports.comparison') }}</v-tab>
              </v-tabs>
              <v-window v-model="combinedTab">
                <v-window-item value="factual">
                  <div v-if="selectedReport.data.factual">
                    <!-- Display factual data similar to monthly_factual -->
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-card variant="outlined" class="mb-3">
                          <v-card-title class="text-subtitle-1">{{ $t('financialReports.transactions') }}</v-card-title>
                          <v-card-text>
                            <div class="d-flex justify-space-between mb-2">
                              <span>{{ $t('financialReports.incoming') }}:</span>
                              <strong>{{ formatNumber(selectedReport.data.factual.transactions?.incoming) }}</strong>
                            </div>
                            <div class="d-flex justify-space-between">
                              <span>{{ $t('financialReports.outgoing') }}:</span>
                              <strong>{{ formatNumber(selectedReport.data.factual.transactions?.outgoing) }}</strong>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-card variant="outlined" class="mb-3">
                          <v-card-title class="text-subtitle-1">{{ $t('financialReports.summary') }}</v-card-title>
                          <v-card-text>
                            <div class="d-flex justify-space-between">
                              <span>{{ $t('financialReports.netChange') }}:</span>
                              <strong>{{ formatNumber(selectedReport.data.factual.summary?.net_change) }}</strong>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </v-window-item>
                <v-window-item value="forecast">
                  <div v-if="selectedReport.data.forecast">
                    <!-- Display forecast data -->
                    <v-alert type="info" class="mb-4" v-if="selectedReport.data.forecast.note">
                      {{ selectedReport.data.forecast.note }}
                    </v-alert>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-card variant="outlined" class="mb-3">
                          <v-card-title class="text-subtitle-1">{{ $t('financialReports.forecastTransactions') }}</v-card-title>
                          <v-card-text>
                            <div class="d-flex justify-space-between mb-2">
                              <span>{{ $t('financialReports.incoming') }}:</span>
                              <strong>{{ formatNumber(selectedReport.data.forecast.transactions?.incoming) }}</strong>
                            </div>
                            <div class="d-flex justify-space-between">
                              <span>{{ $t('financialReports.outgoing') }}:</span>
                              <strong>{{ formatNumber(selectedReport.data.forecast.transactions?.outgoing) }}</strong>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-card variant="outlined" class="mb-3">
                          <v-card-title class="text-subtitle-1">{{ $t('financialReports.forecastSummary') }}</v-card-title>
                          <v-card-text>
                            <div class="d-flex justify-space-between">
                              <span>{{ $t('financialReports.netChange') }}:</span>
                              <strong>{{ formatNumber(selectedReport.data.forecast.summary?.net_change) }}</strong>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </v-window-item>
                <v-window-item value="comparison">
                  <div v-if="selectedReport.data.comparison">
                    <v-alert type="info" class="mb-4" v-if="selectedReport.data.comparison.note">
                      {{ selectedReport.data.comparison.note }}
                    </v-alert>
                    
                    <!-- Transactions Variance -->
                    <v-row v-if="selectedReport.data.comparison.transactions">
                      <v-col cols="12">
                        <v-card variant="outlined" class="mb-3">
                          <v-card-title class="text-subtitle-1">{{ $t('financialReports.transactionsVariance') }}</v-card-title>
                          <v-card-text>
                            <v-row>
                              <v-col cols="12" md="4" v-if="selectedReport.data.comparison.transactions.incoming_variance">
                                <div class="mb-2">
                                  <div class="text-caption text-grey">{{ $t('financialReports.incoming') }}</div>
                                  <div class="d-flex justify-space-between align-center">
                                    <span>{{ $t('financialReports.absolute') }}:</span>
                                    <strong :class="getVarianceColor(selectedReport.data.comparison.transactions.incoming_variance.absolute)">
                                      {{ formatNumber(selectedReport.data.comparison.transactions.incoming_variance.absolute) }}
                                    </strong>
                                  </div>
                                  <div class="d-flex justify-space-between align-center mt-1">
                                    <span>{{ $t('financialReports.percentage') }}:</span>
                                    <strong :class="getVarianceColor(selectedReport.data.comparison.transactions.incoming_variance.percentage)">
                                      {{ formatNumber(selectedReport.data.comparison.transactions.incoming_variance.percentage) }}%
                                    </strong>
                                  </div>
                                </div>
                              </v-col>
                              <v-col cols="12" md="4" v-if="selectedReport.data.comparison.transactions.outgoing_variance">
                                <div class="mb-2">
                                  <div class="text-caption text-grey">{{ $t('financialReports.outgoing') }}</div>
                                  <div class="d-flex justify-space-between align-center">
                                    <span>{{ $t('financialReports.absolute') }}:</span>
                                    <strong :class="getVarianceColor(selectedReport.data.comparison.transactions.outgoing_variance.absolute)">
                                      {{ formatNumber(selectedReport.data.comparison.transactions.outgoing_variance.absolute) }}
                                    </strong>
                                  </div>
                                  <div class="d-flex justify-space-between align-center mt-1">
                                    <span>{{ $t('financialReports.percentage') }}:</span>
                                    <strong :class="getVarianceColor(selectedReport.data.comparison.transactions.outgoing_variance.percentage)">
                                      {{ formatNumber(selectedReport.data.comparison.transactions.outgoing_variance.percentage) }}%
                                    </strong>
                                  </div>
                                </div>
                              </v-col>
                              <v-col cols="12" md="4" v-if="selectedReport.data.comparison.transactions.fees_variance">
                                <div class="mb-2">
                                  <div class="text-caption text-grey">{{ $t('financialReports.fees') }}</div>
                                  <div class="d-flex justify-space-between align-center">
                                    <span>{{ $t('financialReports.absolute') }}:</span>
                                    <strong :class="getVarianceColor(selectedReport.data.comparison.transactions.fees_variance.absolute)">
                                      {{ formatNumber(selectedReport.data.comparison.transactions.fees_variance.absolute) }}
                                    </strong>
                                  </div>
                                  <div class="d-flex justify-space-between align-center mt-1">
                                    <span>{{ $t('financialReports.percentage') }}:</span>
                                    <strong :class="getVarianceColor(selectedReport.data.comparison.transactions.fees_variance.percentage)">
                                      {{ formatNumber(selectedReport.data.comparison.transactions.fees_variance.percentage) }}%
                                    </strong>
                                  </div>
                                </div>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>

                    <!-- Expenses Variance -->
                    <v-row v-if="selectedReport.data.comparison.expenses_variance">
                      <v-col cols="12" md="6">
                        <v-card variant="outlined" class="mb-3">
                          <v-card-title class="text-subtitle-1">{{ $t('financialReports.expensesVariance') }}</v-card-title>
                          <v-card-text>
                            <div class="d-flex justify-space-between align-center mb-2">
                              <span>{{ $t('financialReports.absolute') }}:</span>
                              <strong :class="getVarianceColor(selectedReport.data.comparison.expenses_variance.absolute)">
                                {{ formatNumber(selectedReport.data.comparison.expenses_variance.absolute) }}
                              </strong>
                            </div>
                            <div class="d-flex justify-space-between align-center">
                              <span>{{ $t('financialReports.percentage') }}:</span>
                              <strong :class="getVarianceColor(selectedReport.data.comparison.expenses_variance.percentage)">
                                {{ formatNumber(selectedReport.data.comparison.expenses_variance.percentage) }}%
                              </strong>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>

                    <!-- Settlements Variance -->
                    <v-row v-if="selectedReport.data.comparison.settlements">
                      <v-col cols="12" md="6" v-if="selectedReport.data.comparison.settlements.to_wallet_variance">
                        <v-card variant="outlined" class="mb-3">
                          <v-card-title class="text-subtitle-1">{{ $t('financialReports.toWallet') }} - {{ $t('financialReports.variance') }}</v-card-title>
                          <v-card-text>
                            <div class="d-flex justify-space-between align-center mb-2">
                              <span>{{ $t('financialReports.absolute') }}:</span>
                              <strong :class="getVarianceColor(selectedReport.data.comparison.settlements.to_wallet_variance.absolute)">
                                {{ formatNumber(selectedReport.data.comparison.settlements.to_wallet_variance.absolute) }}
                              </strong>
                            </div>
                            <div class="d-flex justify-space-between align-center">
                              <span>{{ $t('financialReports.percentage') }}:</span>
                              <strong :class="getVarianceColor(selectedReport.data.comparison.settlements.to_wallet_variance.percentage)">
                                {{ formatNumber(selectedReport.data.comparison.settlements.to_wallet_variance.percentage) }}%
                              </strong>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                      <v-col cols="12" md="6" v-if="selectedReport.data.comparison.settlements.to_investor_variance">
                        <v-card variant="outlined" class="mb-3">
                          <v-card-title class="text-subtitle-1">{{ $t('financialReports.toInvestor') }} - {{ $t('financialReports.variance') }}</v-card-title>
                          <v-card-text>
                            <div class="d-flex justify-space-between align-center mb-2">
                              <span>{{ $t('financialReports.absolute') }}:</span>
                              <strong :class="getVarianceColor(selectedReport.data.comparison.settlements.to_investor_variance.absolute)">
                                {{ formatNumber(selectedReport.data.comparison.settlements.to_investor_variance.absolute) }}
                              </strong>
                            </div>
                            <div class="d-flex justify-space-between align-center">
                              <span>{{ $t('financialReports.percentage') }}:</span>
                              <strong :class="getVarianceColor(selectedReport.data.comparison.settlements.to_investor_variance.percentage)">
                                {{ formatNumber(selectedReport.data.comparison.settlements.to_investor_variance.percentage) }}%
                              </strong>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>

                    <!-- Summary Variance -->
                    <v-row v-if="selectedReport.data.comparison.summary">
                      <v-col cols="12" md="6" v-if="selectedReport.data.comparison.summary.net_income_variance">
                        <v-card variant="outlined" class="mb-3">
                          <v-card-title class="text-subtitle-1">{{ $t('financialReports.netIncome') }} - {{ $t('financialReports.variance') }}</v-card-title>
                          <v-card-text>
                            <div class="d-flex justify-space-between align-center mb-2">
                              <span>{{ $t('financialReports.absolute') }}:</span>
                              <strong :class="getVarianceColor(selectedReport.data.comparison.summary.net_income_variance.absolute)">
                                {{ formatNumber(selectedReport.data.comparison.summary.net_income_variance.absolute) }}
                              </strong>
                            </div>
                            <div class="d-flex justify-space-between align-center">
                              <span>{{ $t('financialReports.percentage') }}:</span>
                              <strong :class="getVarianceColor(selectedReport.data.comparison.summary.net_income_variance.percentage)">
                                {{ formatNumber(selectedReport.data.comparison.summary.net_income_variance.percentage) }}%
                              </strong>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                      <v-col cols="12" md="6" v-if="selectedReport.data.comparison.summary.net_change_variance">
                        <v-card variant="tonal" class="mb-3" color="primary">
                          <v-card-title class="text-subtitle-1">{{ $t('financialReports.netChange') }} - {{ $t('financialReports.variance') }}</v-card-title>
                          <v-card-text>
                            <div class="d-flex justify-space-between align-center mb-2">
                              <span>{{ $t('financialReports.absolute') }}:</span>
                              <strong class="text-h6" :class="getVarianceColor(selectedReport.data.comparison.summary.net_change_variance.absolute)">
                                {{ formatNumber(selectedReport.data.comparison.summary.net_change_variance.absolute) }}
                              </strong>
                            </div>
                            <div class="d-flex justify-space-between align-center">
                              <span>{{ $t('financialReports.percentage') }}:</span>
                              <strong class="text-h6" :class="getVarianceColor(selectedReport.data.comparison.summary.net_change_variance.percentage)">
                                {{ formatNumber(selectedReport.data.comparison.summary.net_change_variance.percentage) }}%
                              </strong>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </v-window-item>
              </v-window>
            </div>

            <!-- P&L Report -->
            <div v-else-if="selectedReport.report_type === 'pnl'">
              <v-row>
                <v-col cols="12" md="6">
                  <v-card class="mb-3">
                    <v-card-title class="text-subtitle-1 pb-1">
                      {{ $t('financialReports.profitSection') }}
                    </v-card-title>
                    <v-divider />
                    <v-card-text class="pt-4">
                      <div
                        v-for="(line, index) in selectedReport.data.profit_lines || []"
                        :key="`profit-${index}`"
                        class="d-flex justify-space-between mb-2"
                      >
                        <span>{{ line.name }}:</span>
                        <strong class="text-success">{{ formatNumber(line.amount) }}</strong>
                      </div>
                      <v-divider class="my-2" />
                      <div class="d-flex justify-space-between">
                        <span><strong>{{ $t('financialReports.totalProfit') }}:</strong></span>
                        <strong class="text-success">{{ formatNumber(selectedReport.data.totals?.profit_total) }}</strong>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card class="mb-3">
                    <v-card-title class="text-subtitle-1 pb-1">
                      {{ $t('financialReports.expensesSection') }}
                    </v-card-title>
                    <v-divider />
                    <v-card-text class="pt-4">
                      <div
                        v-for="(line, index) in selectedReport.data.expense_lines || []"
                        :key="`expense-${index}`"
                        class="d-flex justify-space-between mb-2"
                      >
                        <span>{{ line.name }}:</span>
                        <strong class="text-error">{{ formatNumber(line.amount) }}</strong>
                      </div>
                      <v-divider class="my-2" />
                      <div class="d-flex justify-space-between">
                        <span><strong>{{ $t('financialReports.totalExpenses') }}:</strong></span>
                        <strong class="text-error">{{ formatNumber(selectedReport.data.totals?.expenses_total) }}</strong>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12">
                  <v-card>
                    <v-card-title class="text-subtitle-1 pb-1">
                      {{ $t('financialReports.pnlResult') }}
                    </v-card-title>
                    <v-divider />
                    <v-card-text class="pt-4">
                      <div class="d-flex justify-space-between">
                        <span><strong>{{ $t('financialReports.netPL') }}:</strong></span>
                        <strong
                          class="text-h6"
                          :class="(selectedReport.data.totals?.net_pl || 0) >= 0 ? 'text-success' : 'text-error'"
                        >
                          {{ formatNumber(selectedReport.data.totals?.net_pl) }}
                        </strong>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Other Report Types (Fallback to JSON) -->
            <div v-else>
              <v-card variant="outlined">
                <v-card-text>
                  <pre class="report-data">{{ JSON.stringify(selectedReport.data, null, 2) }}</pre>
                </v-card-text>
              </v-card>
            </div>
          </div>
          <div v-else class="text-center pa-4">
            {{ $t('financialReports.noData') }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showViewDialog = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Overlay -->
    <v-overlay v-model="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

const { t } = useI18n()

// Data
const reports = ref<any[]>([])
const loading = ref(false)
const showCreateDialog = ref(false)
const showViewDialog = ref(false)
const editingReport = ref<any>(null)
const selectedReport = ref<any>(null)
const formRef = ref<any>(null)
const formValid = ref(false)
const combinedTab = ref('factual')
const monthlyFactualTab = ref('overview')

// Filters
const filterReportType = ref<string | null>(null)
const filterDateFrom = ref<string | null>(null)
const filterDateTo = ref<string | null>(null)

// Form
const form = ref({
  report_type: '',
  period_start: '',
  period_end: '',
  description: '',
  generate: false
})

// Report types
const reportTypes = computed(() => [
  { label: t('financialReports.monthlyFactual'), value: 'monthly_factual' },
  { label: t('financialReports.weeklyFactual'), value: 'weekly_factual' },
  { label: t('financialReports.monthlyForecast'), value: 'monthly_forecast' },
  { label: t('financialReports.combined'), value: 'combined' },
  { label: t('financialReports.pnl'), value: 'pnl' }
])

// Table headers
const headers = computed(() => [
  { title: t('financialReports.reportType'), key: 'report_type', sortable: true },
  { title: t('financialReports.periodStart'), key: 'period_start', sortable: true },
  { title: t('financialReports.periodEnd'), key: 'period_end', sortable: true },
  { title: t('financialReports.periodDays'), key: 'period_days', sortable: true },
  { title: t('financialReports.generatedAt'), key: 'generated_at', sortable: true },
  { title: t('common.actions'), key: 'actions', sortable: false, width: '150px' }
])

// Validation rules
const rules = {
  required: (v: any) => !!v || 'Required'
}

// Methods
const loadReports = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams()
    if (filterReportType.value) params.append('report_type', filterReportType.value)
    if (filterDateFrom.value) params.append('period_start', filterDateFrom.value)
    if (filterDateTo.value) params.append('period_end', filterDateTo.value)

    const url = `/api/financial-reports${params.toString() ? '?' + params.toString() : ''}`
    const response = await apiGet(url)
    
    if (response.ok) {
      reports.value = await response.json()
    } else {
      console.error('Failed to load reports')
    }
  } catch (error) {
    console.error('Error loading reports:', error)
  } finally {
    loading.value = false
  }
}

const generateMonthlyReport = async () => {
  loading.value = true
  try {
    const today = new Date()
    const year = today.getFullYear()
    const month = today.getMonth() + 1
    
    const response = await apiGet(`/api/financial-reports/monthly_factual?year=${year}&month=${month}`)
    
    if (response.ok) {
      const report = await response.json()
      await loadReports()
      viewReport(report)
    } else {
      console.error('Failed to generate monthly report')
    }
  } catch (error) {
    console.error('Error generating monthly report:', error)
  } finally {
    loading.value = false
  }
}

const generateWeeklyReport = async () => {
  loading.value = true
  try {
    const today = new Date().toISOString().split('T')[0]
    const response = await apiGet(`/api/financial-reports/weekly_factual?date=${today}`)
    
    if (response.ok) {
      const report = await response.json()
      await loadReports()
      viewReport(report)
    } else {
      console.error('Failed to generate weekly report')
    }
  } catch (error) {
    console.error('Error generating weekly report:', error)
  } finally {
    loading.value = false
  }
}

const saveReport = async () => {
  if (!formRef.value?.validate()) return

  loading.value = true
  try {
    const url = editingReport.value 
      ? `/api/financial-reports/${editingReport.value.id}`
      : '/api/financial-reports'
    
    const method = editingReport.value ? apiPut : apiPost
    const body = {
      financial_report: {
        report_type: form.value.report_type,
        period_start: form.value.period_start,
        period_end: form.value.period_end,
        description: form.value.description
      },
      generate: form.value.generate ? 'true' : 'false'
    }

    const response = await method(url, body)
    
    if (response.ok) {
      closeDialog()
      await loadReports()
    } else {
      const error = await response.json()
      console.error('Failed to save report:', error)
    }
  } catch (error) {
    console.error('Error saving report:', error)
  } finally {
    loading.value = false
  }
}

const viewReport = async (report: any) => {
  loading.value = true
  try {
    const response = await apiGet(`/api/financial-reports/${report.id}`)
    
    if (response.ok) {
      selectedReport.value = await response.json()
      monthlyFactualTab.value = 'overview'
      showViewDialog.value = true
    } else {
      console.error('Failed to load report details')
    }
  } catch (error) {
    console.error('Error loading report details:', error)
  } finally {
    loading.value = false
  }
}

const regenerateReport = async (report: any) => {
  if (!confirm(t('financialReports.confirmRegenerate'))) return

  loading.value = true
  try {
    const response = await apiPost(`/api/financial-reports/${report.id}/regenerate`)
    
    if (response.ok) {
      await loadReports()
    } else {
      console.error('Failed to regenerate report')
    }
  } catch (error) {
    console.error('Error regenerating report:', error)
  } finally {
    loading.value = false
  }
}

const deleteReport = async (report: any) => {
  if (!confirm(t('financialReports.confirmDelete'))) return

  loading.value = true
  try {
    const response = await apiDelete(`/api/financial-reports/${report.id}`)
    
    if (response.ok) {
      await loadReports()
    } else {
      console.error('Failed to delete report')
    }
  } catch (error) {
    console.error('Error deleting report:', error)
  } finally {
    loading.value = false
  }
}

const closeDialog = () => {
  showCreateDialog.value = false
  editingReport.value = null
  form.value = {
    report_type: '',
    period_start: '',
    period_end: '',
    description: '',
    generate: false
  }
  formRef.value?.resetValidation()
}

const getReportTypeLabel = (type: string) => {
  const typeMap: Record<string, string> = {
    monthly_factual: t('financialReports.monthlyFactual'),
    weekly_factual: t('financialReports.weeklyFactual'),
    monthly_forecast: t('financialReports.monthlyForecast'),
    combined: t('financialReports.combined'),
    pnl: t('financialReports.pnl')
  }
  return typeMap[type] || type
}

const getReportTypeColor = (type: string) => {
  const colorMap: Record<string, string> = {
    monthly_factual: 'primary',
    weekly_factual: 'info',
    monthly_forecast: 'warning',
    combined: 'success',
    pnl: 'deep-purple'
  }
  return colorMap[type] || 'default'
}

const formatDate = (date: string) => {
  if (!date) return '-'
  return new Date(date).toLocaleDateString()
}

const formatDateTime = (dateTime: string) => {
  if (!dateTime) return '-'
  return new Date(dateTime).toLocaleString()
}

const formatPeriod = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) return '-'
  const start = new Date(`${startDate}T00:00:00`)
  const end = new Date(`${endDate}T00:00:00`)

  const isFullMonthRange = (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === 1 &&
    end.getDate() === new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate()
  )

  if (isFullMonthRange) {
    return start.toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric'
    })
  }

  const formatOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }
  return `${start.toLocaleDateString(undefined, formatOptions)} - ${end.toLocaleDateString(undefined, formatOptions)}`
}

const formatNumber = (value: number | string | null | undefined) => {
  if (value === null || value === undefined) return '0'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '0'
  return num.toLocaleString(undefined, { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 8 
  })
}

const getVarianceColor = (value: number | null | undefined) => {
  if (value === null || value === undefined) return ''
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return ''
  
  // Positive variance (actual > forecast) = good = green
  // Negative variance (actual < forecast) = bad = red
  // Zero = neutral
  if (num > 0) return 'text-success'
  if (num < 0) return 'text-error'
  return ''
}

// Lifecycle
onMounted(() => {
  loadReports()
})
</script>

<style scoped>
.report-data {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  max-height: 500px;
  overflow-y: auto;
}

.v-theme--dark .report-data {
  background-color: #1e1e1e;
}
</style>
